import { BorderedContainer } from 'core/components/common/BorderedContainer';
import {
  convertToPresenceAbsence,
  formatDateTime,
} from 'core/components/common/converters';
import { SectionTitle } from 'core/components/common/SectionTitle';
import { InfoDisplay } from 'core/components/JianDetail/common/InfoDisplay';
import { IncidentDetailPatient } from 'sections/ube/features/IncidentDetail/useIncidentDetailForUbe';
import { AccordionToggle } from 'core/components/JianDetail/PatientDetailInfo/common/AccordionToggle';
import { AccordionToggleContent } from 'core/components/JianDetail/PatientDetailInfo/common/AccordionToggleContent';
import { useOpenClose } from 'core/hooks/useOpenClose';

type Props = {
  /** バイタルリスト。 */
  vitals: IncidentDetailPatient['vitals'];
};

export const VitalsView = ({ vitals }: Props) => {
  return (
    <BorderedContainer>
      <div className="flex flex-col gap-[24px]">
        <SectionTitle text="バイタル" />
        <div className="flex flex-col gap-[8px]">
          {vitals.length > 0 ? (
            vitals.map(vital => (
              <VitalDetailView key={vital.vitalId} vitalData={vital.data} />
            ))
          ) : (
            <div>
              <p>データが存在しません</p>
            </div>
          )}
        </div>
      </div>
    </BorderedContainer>
  );
};

type VitalDetailViewProps = {
  /** バイタル。 */
  vitalData: IncidentDetailPatient['vitals'][number]['data'];
};

const VitalDetailView = ({ vitalData }: VitalDetailViewProps) => {
  const { isOpen, handleOpen, handleClose } = useOpenClose();

  return (
    <>
      <AccordionToggle
        isOpen={isOpen}
        onOpen={handleOpen}
        onClose={handleClose}
      >
        <p className="text-[16px] noto-sans-jp-medium text-textPrimary">
          {vitalData.recordedAt
            ? formatDateTime(vitalData.recordedAt)
            : '時間未指定'}
        </p>
      </AccordionToggle>
      <AccordionToggleContent isOpen={isOpen}>
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay title="JCS" content={vitalData.jcs ?? ''} />
          <InfoDisplay
            title="R"
            content={convertToPresenceAbsence(vitalData.riaR)}
          />
          <InfoDisplay
            title="I"
            content={convertToPresenceAbsence(vitalData.riaI)}
          />
          <InfoDisplay
            title="A"
            content={convertToPresenceAbsence(vitalData.riaA)}
          />
        </div>
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay title="E(GCS)" content={vitalData.gcsE ?? ''} />
          <InfoDisplay title="V(GCS)" content={vitalData.gcsV ?? ''} />
          <InfoDisplay title="M(GCS)" content={vitalData.gcsM ?? ''} />
        </div>
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay
            title="脈拍"
            content={vitalData.pr ? `${vitalData.pr} 回/min` : ''}
          />
          <InfoDisplay
            title="波形"
            content={getEcgWaveformLabelForUbe(vitalData.ecgWaveform)}
          />
        </div>
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay title="血圧/収縮" content={vitalData.sbpRight ?? ''} />
          <InfoDisplay title="血圧/拡張" content={vitalData.dbpRight ?? ''} />
          <InfoDisplay title="部位" content={vitalData.bpPart ?? ''} />
        </div>
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay
            title="呼吸"
            content={vitalData.rr ? `${vitalData.rr} 回/min` : ''}
          />
          <InfoDisplay
            title="聴診"
            content={
              vitalData.observations.includes('AUSCULTATION') ? '該当' : ''
            }
          />
        </div>
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay
            title="SpO2(投与前)"
            content={vitalData.spo2 ? `${vitalData.spo2} %` : ''}
          />
          <InfoDisplay title="条件" content={vitalData.o2FlowCondition} />
          <InfoDisplay
            title="酸素投与"
            content={vitalData.o2Flow ? `${vitalData.o2Flow} L/min` : ''}
          />
        </div>
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay
            title="体温"
            content={vitalData.bt ? `${vitalData.bt} °C` : ''}
          />
          <InfoDisplay title="部位" content={vitalData.btPart} />
        </div>
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay
            title="左瞳(左)"
            content={
              vitalData.pupilSizeLeft ? `${vitalData.pupilSizeLeft} mm` : ''
            }
          />
          <InfoDisplay
            title="対光反射(左)"
            content={vitalData.pupilReactionLeft}
          />
          <InfoDisplay
            title="左瞳(右)"
            content={
              vitalData.pupilSizeRight ? `${vitalData.pupilSizeRight} mm` : ''
            }
          />
          <InfoDisplay
            title="対光反射(右)"
            content={vitalData.pupilReactionRight}
          />
        </div>
        <div>
          <InfoDisplay title="その他" content={vitalData.vitalNote ?? ''} />
        </div>
      </AccordionToggleContent>
    </>
  );
};

const getEcgWaveformLabelForUbe = (ecgWaveform: string) => {
  switch (ecgWaveform) {
    case '心律不整':
      return '不整';
    case '心室細動':
      return 'VF';
    case '心室頻拍':
      return 'VT';
    case 'PEA':
      return 'PEA';
    case '心静止':
      return 'Asys';
    default:
      return ecgWaveform;
  }
};
